import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { CalendarServiceEvent, EligibleGroupMeeting, Meeting } from "features/Resources/Meeting/models";
import { apiMeetingById, v2ApiNewHireMeetingsPathById } from "helpers/url/api/v2";
import { NewHireJourneyId } from "models/newHire";
import { UserId } from "features/User/models";

export const meetingKeys = {
  all: ["meetings"] as const,
  lists: () => [...meetingKeys.all, "list"] as const,
  list: (filters: string) => [...meetingKeys.lists(), { filters }] as const,
  details: () => [...meetingKeys.all, "detail"] as const,
  detail: (id: string) => [...meetingKeys.details(), id] as const,
  byNewHireJourneys: () => [...meetingKeys.all, "newHireJourneys"] as const,
  byNewHireJourney: (id: string) => [...meetingKeys.byNewHireJourneys(), id] as const,
};

export function setNewMeetingInQueryClient(queryClient: QueryClient, newMeeting: Meeting) {
  queryClient.setQueryData(meetingKeys.detail(newMeeting.id), (oldData: Meeting | undefined) => {
    if (!oldData) {
      return newMeeting;
    }
    return {
      ...oldData,
      ...newMeeting,
    };
  });
}

export const useMeetingDetailQuery = (id: string | undefined, options = {}) => useQuery<Meeting | null>({
  queryKey: meetingKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiMeetingById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useMeetingListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<Meeting[]>({
    queryKey: queryString ? meetingKeys.list(queryString) : meetingKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.meetings.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

export const useNewHireJourneyMeetingsQuery = (newHireJourneyId: NewHireJourneyId, options = {}) => useQuery<Meeting[]>({
  queryKey: meetingKeys.byNewHireJourney(newHireJourneyId),
  queryFn: async () => apiClient.get(v2ApiNewHireMeetingsPathById(newHireJourneyId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!newHireJourneyId && ((options as { enabled?: boolean }).enabled ?? true),
});

function useMeetingRequest(
  mutationFn: (data: any) => Promise<any>,
  resetQueryClient: boolean = false,
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      if (resetQueryClient) {
        queryClient.invalidateQueries({ queryKey: meetingKeys.all });
        // Availability is cached per meeting/day under [meetingId, "availability", …], outside
        // meetingKeys — so attendee/schedule/lead changes must invalidate it explicitly, or the
        // Adjust-time picker keeps showing a stale participant list.
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[1] === "availability" });
      } else {
        setNewMeetingInQueryClient(queryClient, res);
      }
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export const useAvailableSessionsQuery = (meetingId: string, options = {}) => useQuery<CalendarServiceEvent[]>({
  queryKey: [...meetingKeys.detail(meetingId), "availableSessions"],
  queryFn: async () => apiClient.get(`${apiMeetingById(meetingId)}/available-sessions`),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!meetingId && ((options as { enabled?: boolean }).enabled ?? true),
});

export function useAddToSession(meetingId: string) {
  return useMeetingRequest(async (data: { eventId: string; }) => apiClient.post<Meeting>(`${apiMeetingById(meetingId)}/available-sessions`, data), true);
}

export function useUpdateMeeting(meetingId: string) {
  return useMeetingRequest(async (data: Meeting) => apiClient.put<Meeting>(apiMeetingById(meetingId), { meeting: data }));
}

export function useSetMeetingLead(meetingId: string) {
  // Reassigns across the whole session — invalidate rather than merge so every sibling
  // row's lead + attendees (and the availability picker) refresh together.
  return useMeetingRequest(async (data: { leadId: UserId }) =>
    apiClient.patch<Meeting>(`${apiMeetingById(meetingId)}/lead`, data), true);
}

export function useScheduleMeeting(meetingId: string) {
  return useMeetingRequest(async (data: { scheduleAt: string; timezone?: string; additionalMeetingIds?: string[] }) => apiClient.post<Meeting>(`${apiMeetingById(meetingId)}/schedule`, data), true);
}

export const useEligibleForGroupQuery = (meetingId: string, options = {}) => useQuery<EligibleGroupMeeting[]>({
  queryKey: [...meetingKeys.detail(meetingId), "eligibleForGroup"],
  queryFn: async () => apiClient.get(`${apiMeetingById(meetingId)}/eligible-for-group`),
  staleTime: 1000 * 60 * 3,
  ...options,
  enabled: !!meetingId && ((options as { enabled?: boolean }).enabled ?? true),
});

export function useUnscheduleMeeting(meetingId: string) {
  return useMeetingRequest(async () => apiClient.delete<Meeting>(`${apiMeetingById(meetingId)}/schedule`), true);
}

export function useCreateMeeting() {
  return useMeetingRequest(async (data: Meeting) => apiClient.post<Meeting>(API_ROUTES.meetings.base, { meeting: data }), true);
}

export function useAddAttendeeToMeeting(meetingId: string) {
  // Endpoint returns only { success, id, status }, so invalidate rather than merge — refreshes
  // the meeting detail and the by-journey list (attendee avatars) together.
  return useMeetingRequest(async (data: { userId: UserId } | { email: string; emailType?: string }) =>
    apiClient.post<Meeting>(`${apiMeetingById(meetingId)}/add-attendee`, data), true);
}

export function useRemoveAttendeeFromMeeting(meetingId: string) {
  return useMeetingRequest(async (data: { userId: UserId }) =>
    apiClient.post<Meeting>(`${apiMeetingById(meetingId)}/remove-attendee`, data), true);
}

export function useCreateCalendarEvent(meetingId: string) {
  return useMeetingRequest(async () => apiClient.post<{ externalUrl: string; }>(`${apiMeetingById(meetingId)}/calendar-event`, {}), true);
}
